import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  // Legacy Stripe param
  const sessionId = params.get("session_id");
  // Viva: backend redirects with ?order_id=...&viva=1 after success verification.
  // Direct Viva return (if you ever point success URL straight to FE) provides t & s.
  const orderIdParam = params.get("order_id");
  const vivaT = params.get("t");
  const vivaS = params.get("s");
  const { t } = useLang();
  const [statusState, setStatusState] = useState("processing"); // processing | paid | timeout | placed | failed
  const [order, setOrder] = useState(null);
  const attemptsRef = useRef(0);

  // Load order details when we have an order_id
  useEffect(() => {
    if (orderIdParam) {
      api.get(`/orders/${orderIdParam}`).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [orderIdParam]);

  // Stripe legacy polling
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 8) {
        setStatusState("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatusState("paid");
          const oid = res.data.metadata?.order_id;
          if (oid) {
            api.get(`/orders/${oid}`).then((r) => setOrder(r.data)).catch(() => {});
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatusState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Viva polling — backend has typically already verified via redirect handler,
  // but we re-check the order to update the UI to "paid" once webhook/redirect finishes.
  useEffect(() => {
    if (sessionId) return; // stripe branch handles it
    let cancelled = false;
    if (!orderIdParam && !vivaS) {
      // Non-card flow (cod / bank / paypal) — show "placed" once.
      const timer = setTimeout(() => { if (!cancelled) setStatusState("placed"); }, 0);
      return () => { cancelled = true; clearTimeout(timer); };
    }
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 10) {
        setStatusState("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        if (orderIdParam) {
          const r = await api.get(`/orders/${orderIdParam}`);
          if (cancelled) return;
          setOrder(r.data);
          if (r.data?.payment_status === "paid") {
            setStatusState("paid");
            return;
          }
          if (r.data?.payment_method && !["card", "stripe"].includes(r.data.payment_method)) {
            // Non-card → show placed
            setStatusState("placed");
            return;
          }
        }
        if (vivaS) {
          const s = await api.get(`/checkout/viva/status/${vivaS}`);
          if (cancelled) return;
          if (s.data?.payment_status === "paid" || s.data?.order_payment_status === "paid") {
            setStatusState("paid");
            return;
          }
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderIdParam, vivaS, vivaT]);

  return (
    <div data-testid="success-page" className="max-w-[800px] mx-auto px-6 py-24 text-center">
      {statusState === "processing" && (
        <>
          <Clock size={48} className="mx-auto mb-6 text-[#D4AF37] animate-pulse" />
          <h1 className="font-serif text-4xl mb-4">{t("success.processing")}</h1>
          <p className="text-[#736B66] dark:text-[#B3B3B3]">{t("success.processingDesc") || ""}</p>
        </>
      )}
      {(statusState === "paid" || statusState === "placed") && (
        <>
          <CheckCircle2 size={56} className="mx-auto mb-6 text-[#D4AF37]" />
          <h1 className="font-serif text-4xl mb-4" data-testid="success-title">{t("success.title")}</h1>
          <p className="text-[#736B66] dark:text-[#B3B3B3] mb-2">{statusState === "paid" ? t("success.paid") : t("success.pending")}</p>
          {order?.id && (
            <p className="text-sm" data-testid="order-id-display">
              {t("success.orderNumber")}: <span className="font-mono">{order.id}</span>
            </p>
          )}
          {order?.payment_method === "bank_transfer" && (
            <div className="mt-8 bg-[#F5F2ED] p-6 text-left max-w-md mx-auto" data-testid="bank-details">
              <h3 className="font-serif text-xl mb-3">{t("success.bankDetails")}</h3>
              <p className="text-sm mb-1"><strong>{t("success.bank") || "Τράπεζα"}:</strong> Viva Bank</p>
              <p className="text-sm mb-1"><strong>{t("success.iban")}:</strong> GR66 0570 0000 0009 8697 5923 177</p>
              <p className="text-sm mb-1"><strong>{t("success.beneficiary")}:</strong> Σαριδόπουλος Δημήτριος</p>
              <p className="text-sm"><strong>{t("success.reference")}:</strong> {order.id}</p>
            </div>
          )}
        </>
      )}
      {statusState === "timeout" && (
        <>
          <AlertTriangle size={48} className="mx-auto mb-6 text-[#D4AF37]" />
          <h1 className="font-serif text-4xl mb-4">{t("success.timeout")}</h1>
          <p className="text-[#736B66] dark:text-[#B3B3B3] mb-2">{t("success.timeoutDesc") || ""}</p>
          {order?.id && (
            <p className="text-sm mb-4" data-testid="order-id-display">
              {t("success.orderNumber")}: <span className="font-mono">{order.id}</span>
            </p>
          )}
        </>
      )}
      <Link to="/shop" className="luxury-btn mt-10">{t("success.continueShopping")}</Link>
    </div>
  );
};

export default CheckoutSuccess;
